import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Sidebar from "./Sidebar";
import ForceChangePasswordGate from "./ForceChangePasswordGate";
import { Menu, Search, Bell, Sun, Moon, Package, FileText, ClipboardList, Boxes, Tag, Inbox } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Toaster, toast } from "sonner";
import { api } from "@/lib/api";
import { fmtIST } from "@/lib/datetime";

const GROUP_ICON = {
  Enquiries: FileText,
  Bulletins: ClipboardList,
  Samples: Package,
  Orders: Boxes,
  Styles: Tag,
};

function useTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem("opsiq_theme") || "light";
  });
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("opsiq_theme", theme);
  }, [theme]);
  return [theme, () => setTheme((t) => (t === "dark" ? "light" : "dark"))];
}

export default function Layout() {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, toggleTheme] = useTheme();
  const nav = useNavigate();

  // ── Global search ──
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchBusy, setSearchBusy] = useState(false);
  const searchWrapRef = useRef(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const q = searchQ.trim();
    if (q.length < 2) { setSearchResults([]); setSearchOpen(false); return; }
    searchTimer.current = setTimeout(async () => {
      setSearchBusy(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setSearchResults(data.results || []);
        setSearchOpen(true);
      } catch { /* silent */ } finally { setSearchBusy(false); }
    }, 350);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [searchQ]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) setSearchOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setSearchOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDocClick); document.removeEventListener("keydown", onEsc); };
  }, []);

  const goResult = (r) => {
    setSearchOpen(false);
    setSearchQ("");
    setSearchResults([]);
    nav(r.link);
  };

  // Group results for display, preserving order of first appearance.
  const grouped = searchResults.reduce((acc, r) => {
    (acc[r.group] = acc[r.group] || []).push(r);
    return acc;
  }, {});

  // ── Notifications bell ──
  const [notes, setNotes] = useState({ items: [], unread: 0 });
  const [notesOpen, setNotesOpen] = useState(false);

  const loadNotes = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotes(data);
    } catch { /* silent */ }
  };
  useEffect(() => {
    loadNotes();
    const t = setInterval(loadNotes, 60000);
    return () => clearInterval(t);
  }, []);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-read");
      setNotes((n) => ({ ...n, unread: 0, items: n.items.map((i) => ({ ...i, read: true })) }));
    } catch { toast.error("Could not mark read"); }
  };

  const today = new Date().toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <div className="hidden lg:block">
        <div className="fixed inset-y-0 left-0 z-30">
          <Sidebar user={user} />
        </div>
      </div>

      {/* Mobile sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64 bg-sidebar border-r-0">
          <Sidebar user={user} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 lg:pl-64 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-background/90 backdrop-blur border-b border-border">
          <div className="flex items-center gap-3 px-4 lg:px-8 h-16">
            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-slate-100 dark:hover:bg-white/5"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </button>

            <div className="hidden md:flex items-center gap-2 flex-1 max-w-lg">
              <div className="relative w-full" ref={searchWrapRef}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  data-testid="global-search"
                  placeholder="Search enquiries, styles, orders..."
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  onFocus={() => searchResults.length && setSearchOpen(true)}
                  className="w-full h-9 pl-9 pr-3 rounded-md bg-slate-50 dark:bg-white/5 border border-border text-sm outline-none focus:border-slate-300 dark:focus:border-white/20 focus:bg-white dark:focus:bg-white/10 transition-colors"
                />
                {searchOpen && (
                  <div
                    data-testid="global-search-results"
                    className="absolute top-11 left-0 right-0 max-h-[420px] overflow-y-auto rounded-md border border-slate-200 dark:border-white/10 bg-white dark:bg-zinc-900 shadow-xl z-50"
                  >
                    {searchBusy && searchResults.length === 0 && (
                      <div className="px-4 py-3 text-[12px] text-slate-400">Searching…</div>
                    )}
                    {!searchBusy && searchResults.length === 0 && (
                      <div className="px-4 py-3 text-[12px] text-slate-400">No matches for “{searchQ.trim()}”</div>
                    )}
                    {Object.entries(grouped).map(([group, rows]) => {
                      const Icon = GROUP_ICON[group] || Inbox;
                      return (
                        <div key={group}>
                          <div className="px-3 pt-2.5 pb-1 text-[10px] font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                            <Icon className="h-3 w-3" /> {group}
                          </div>
                          {rows.map((r) => (
                            <button
                              key={`${group}-${r.id}`}
                              type="button"
                              onClick={() => goResult(r)}
                              data-testid={`search-result-${r.id}`}
                              className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-white/5 flex flex-col"
                            >
                              <span className="text-[13px] font-medium text-slate-800 dark:text-slate-100 truncate">{r.label}</span>
                              {r.sub && <span className="text-[11px] text-slate-500 truncate">{r.sub}</span>}
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 md:hidden" />

            {/* Right cluster — date · theme · bell */}
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex flex-col items-end text-right leading-tight mr-1" data-testid="topbar-date">
                <div className="text-[12.5px] font-medium text-slate-700 dark:text-slate-200">{today}</div>
                <div className="text-[10px] text-slate-400 dark:text-slate-500 font-mono tracking-wider uppercase">IST</div>
              </div>

              <button
                onClick={toggleTheme}
                data-testid="theme-toggle-btn"
                aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-slate-100 dark:hover:bg-white/5 text-slate-600 dark:text-slate-300 transition-colors"
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>

              <Popover open={notesOpen} onOpenChange={setNotesOpen}>
                <PopoverTrigger asChild>
                  <button
                    className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-slate-100 dark:hover:bg-white/5 relative text-slate-600 dark:text-slate-300"
                    data-testid="notifications-btn"
                    aria-label="Notifications"
                  >
                    <Bell className="h-4 w-4" />
                    {notes.unread > 0 && (
                      <span
                        data-testid="notifications-badge"
                        className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-brand text-white text-[9.5px] font-bold flex items-center justify-center"
                      >
                        {notes.unread > 99 ? "99+" : notes.unread}
                      </span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[360px] p-0" data-testid="notifications-panel">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <div className="text-[13px] font-semibold">Notifications</div>
                    {notes.unread > 0 && (
                      <button
                        onClick={markAllRead}
                        className="text-[11px] text-brand hover:underline"
                        data-testid="notifications-mark-read"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-[380px] overflow-y-auto divide-y divide-slate-100 dark:divide-white/5">
                    {notes.items.length === 0 && (
                      <div className="px-4 py-8 text-center text-[12px] text-slate-400">
                        You're all caught up — no notifications.
                      </div>
                    )}
                    {notes.items.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => { setNotesOpen(false); nav(n.link); }}
                        data-testid={`notification-${n.id}`}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-white/5 ${n.read ? "" : "bg-brand-soft/40"}`}
                      >
                        <div className="flex items-start gap-2">
                          {!n.read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-brand shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <div className={`text-[12.5px] leading-snug ${n.read ? "text-slate-600 dark:text-slate-300" : "font-semibold text-slate-800 dark:text-slate-100"}`}>
                              {n.message}
                            </div>
                            <div className="text-[10.5px] text-slate-400 mt-1">{fmtIST(n.created_at)}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 lg:px-8 py-6 lg:py-8 min-w-0">
          <Outlet />
        </main>
      </div>

      <Toaster position="top-right" richColors />
      <ForceChangePasswordGate />
    </div>
  );
}
